import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, GoogleMapProps, InfoWindow, Marker, Polyline, withGoogleMap } from 'react-google-maps';

import { DateTime } from 'luxon';
import axios from 'axios';

import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  MAP_STYLES,
  VEHICLE_PATH_COLORS,
} from '@admin/helpers/google_map_constants';
import DEPOT from '@admin/images/maps/markers/depot.svg';
import PICKUP from '@admin/images/maps/markers/pickup.svg';
import PROGRESS from '@admin/images/maps/markers/progress.svg';
import RETURN from '@admin/images/maps/markers/return.svg';
import VEHICLE from '@admin/images/maps/markers/vehicle.svg';
import WAREHOUSE from '@admin/images/maps/markers/warehouse.svg';
import { OrderTypeEnum, Assignment__Role } from '@admin/schema';

type Address = {
  latitude?: number | string | null;
  longitude?: number | string | null;
  street?: string | null;
  aptsuite?: string | null;
};

type MapOrder = {
  id: string;
  number: string;
  type: OrderTypeEnum;
  scheduled: string;
  address: Address;
  account: {
    customer: { name: string };
  };
};

type MapVehicle = {
  id: string;
  name: string;
};

type MapAssignment = {
  role: Assignment__Role | string;
  user: {
    id: string;
    name: string;
  };
};

type Checkin = {
  id: number;
  latitude: number;
  longitude: number;
};

type MapMarker = {
  id: string;
  position: { lat: number; lng: number };
  icon: { url: string; scaledSize?: google.maps.Size; anchor?: google.maps.Point };
  notes: string;
};

type MapPolyline = {
  id: string;
  path: { lat: number; lng: number }[];
  color: string;
};

type MapInfo = {
  depot: { id: number; name: string; address: Address };
  warehouse: { id: number; name: string; address: Address };
};

const GoogleMapWithJS = withGoogleMap(GoogleMap);

const GoogleMapWithDOM: React.FC<GoogleMapProps> = (props) => (
  <GoogleMapWithJS
    {...props}
    containerElement={<div style={{ height: '400px' }} />}
    mapElement={<div style={{ height: '100%' }} />}
  />
);

const pollingIntervalSeconds = () =>
  (window as { clutter?: { dispatchMapPollingInterval?: number } }).clutter?.dispatchMapPollingInterval ?? 30;

const fetchCheckins = async (
  resourceType: 'users' | 'vehicles',
  resourceID: string,
  date: string,
  sinceID?: number,
) => {
  const response = await axios.get<Checkin[]>(`/${resourceType}/${resourceID}/checkins.json`, {
    params: { date, ...(sinceID ? { since_id: sinceID } : {}) },
  });
  return response.data;
};

const markerIcon = (url: string, size = new google.maps.Size(24, 32)) => ({
  url,
  scaledSize: size,
  origin: new google.maps.Point(0, 0),
  anchor: new google.maps.Point(12, 32),
});

const buildStaticMarker = (
  id: string,
  address: Address,
  notes: string[],
  iconUrl: string,
  iconSize?: google.maps.Size,
): MapMarker | null => {
  if (!address.latitude || !address.longitude) return null;
  return {
    id,
    position: { lat: Number(address.latitude), lng: Number(address.longitude) },
    icon: markerIcon(iconUrl, iconSize),
    notes: notes.join('\n'),
  };
};

export const DispatchMap: React.FC<{
  dispatchID: string;
  arrival: string;
  orders: MapOrder[];
  vehicles: MapVehicle[];
  assignments: MapAssignment[];
}> = ({ dispatchID, arrival, orders, vehicles, assignments }) => {
  const [mapInfo, setMapInfo] = useState<MapInfo | undefined>();
  const [markers, setMarkers] = useState<MapMarker[]>([]);
  const [polylines, setPolylines] = useState<MapPolyline[]>([]);
  const [activeMarkerID, setActiveMarkerID] = useState<string | undefined>();
  const polylinePathsRef = useRef<Record<string, { lat: number; lng: number }[]>>({});
  const polylineLatestIdsRef = useRef<Record<string, number | null>>({});

  const date = useMemo(() => DateTime.fromISO(arrival).toFormat('yyyy-MM-dd'), [arrival]);

  const center = useMemo(() => {
    if (!markers.length) {
      return { lat: DEFAULT_LATITUDE, lng: DEFAULT_LONGITUDE };
    }
    const lats = markers.map((marker) => marker.position.lat);
    const lngs = markers.map((marker) => marker.position.lng);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [markers]);

  const drawPolylines = useCallback((id: string, coordinates: { lat: number; lng: number }[], color: string) => {
    polylinePathsRef.current[id] = coordinates;
    setPolylines((current) => {
      const index = current.findIndex((polyline) => polyline.id === id);
      if (index === -1) {
        return [...current, { id, path: coordinates, color }];
      }
      const updated = [...current];
      updated[index] = { id, path: coordinates, color };
      return updated;
    });
  }, []);

  const updateMarker = useCallback((marker: MapMarker) => {
    setMarkers((current) => {
      const index = current.findIndex((existing) => existing.id === marker.id);
      if (index === -1) return [...current, marker];
      const updated = [...current];
      updated[index] = marker;
      return updated;
    });
  }, []);

  useEffect(() => {
    axios.get<MapInfo>(`/dispatches/${dispatchID}/map.json`).then((response) => setMapInfo(response.data));
  }, [dispatchID]);

  useEffect(() => {
    if (!mapInfo) return;

    const staticMarkers = [
      buildStaticMarker(`depot:${mapInfo.depot.id}`, mapInfo.depot.address, [mapInfo.depot.name], DEPOT),
      buildStaticMarker(
        `warehouse:${mapInfo.warehouse.id}`,
        mapInfo.warehouse.address,
        [mapInfo.warehouse.name],
        WAREHOUSE,
      ),
      ...orders.map((order) => {
        const icon = order.type === OrderTypeEnum.Pickup ? PICKUP : RETURN;
        const scheduled = DateTime.fromISO(order.scheduled).toLocaleString(DateTime.TIME_SIMPLE);
        const notes = [
          `${order.account.customer.name} (#${order.number})`,
          `Scheduled Time: ${scheduled}`,
          [order.address.street, order.address.aptsuite].filter(Boolean).join(', '),
        ];
        return buildStaticMarker(`order:${order.id}`, order.address, notes, icon);
      }),
    ].filter((marker): marker is MapMarker => marker !== null);

    setMarkers(staticMarkers);
  }, [mapInfo, orders]);

  useEffect(() => {
    if (!mapInfo) return;

    const intervals: number[] = [];

    const refreshVehicle = (vehicle: MapVehicle, polylineColor: string) => {
      const polylineId = `vehicle:${vehicle.id}:polyline`;

      const tick = async () => {
        const sinceId = polylineLatestIdsRef.current[polylineId] ?? undefined;
        const points = await fetchCheckins('vehicles', vehicle.id, date, sinceId);
        const lastCheckin = points[points.length - 1];

        if (lastCheckin) {
          const marker = buildStaticMarker(
            `vehicle:${vehicle.id}`,
            lastCheckin,
            [vehicle.name],
            VEHICLE,
            new google.maps.Size(24, 20),
          );
          if (marker) updateMarker(marker);
        }

        const coordinates = polylinePathsRef.current[polylineId] ?? [];
        const newCoords = coordinates.concat(
          points.map((point) => ({ lat: Number(point.latitude), lng: Number(point.longitude) })),
        );
        polylineLatestIdsRef.current[polylineId] = lastCheckin?.id ?? polylineLatestIdsRef.current[polylineId] ?? null;
        drawPolylines(polylineId, newCoords, polylineColor);
      };

      tick();
      intervals.push(window.setInterval(tick, pollingIntervalSeconds() * 1000));
    };

    const refreshLead = (leadUser: MapAssignment['user']) => {
      const polylineId = `user:${leadUser.id}:polyline`;

      const tick = async () => {
        const sinceId = polylineLatestIdsRef.current[polylineId] ?? undefined;
        const points = await fetchCheckins('users', leadUser.id, date, sinceId);
        const lastCheckin = points[points.length - 1];

        if (lastCheckin) {
          const marker = buildStaticMarker(
            `user:${leadUser.id}`,
            lastCheckin,
            ['Lead', leadUser.name],
            PROGRESS,
            new google.maps.Size(20, 28),
          );
          if (marker) updateMarker(marker);
        }

        const coordinates = polylinePathsRef.current[polylineId] ?? [];
        const newCoords = coordinates.concat(
          points.map((point) => ({ lat: Number(point.latitude), lng: Number(point.longitude) })),
        );
        polylineLatestIdsRef.current[polylineId] = lastCheckin?.id ?? polylineLatestIdsRef.current[polylineId] ?? null;
        drawPolylines(polylineId, newCoords, '#F39C12');
      };

      tick();
      intervals.push(window.setInterval(tick, pollingIntervalSeconds() * 1000));
    };

    const initializePaths = async () => {
      const vehiclePointCounts = await Promise.all(
        vehicles.map((vehicle) => fetchCheckins('vehicles', vehicle.id, date).then((points) => points.length)),
      );
      const totalVehiclePoints = vehiclePointCounts.reduce((sum, count) => sum + count, 0);

      if (totalVehiclePoints > 3) {
        vehicles.forEach((vehicle, index) => {
          refreshVehicle(vehicle, VEHICLE_PATH_COLORS[index % VEHICLE_PATH_COLORS.length]);
        });
      } else {
        const leadAssignment = assignments.find((assignment) => assignment.role === Assignment__Role.Lead);
        if (leadAssignment) {
          refreshLead(leadAssignment.user);
        }
      }
    };

    initializePaths();

    return () => intervals.forEach((interval) => window.clearInterval(interval));
  }, [assignments, date, drawPolylines, mapInfo, updateMarker, vehicles]);

  const activeMarker = markers.find((marker) => marker.id === activeMarkerID);

  return (
    <>
      <div className="legend">
        <div className="legend-entry legend-entry-vehicle-route">Vehicle Route</div>
        <div className="legend-entry legend-entry-working">Lead Route</div>
        <div className="legend-entry legend-entry-pickup">Pickup</div>
        <div className="legend-entry legend-entry-return">Return</div>
      </div>
      <GoogleMapWithDOM center={center} zoom={11} options={{ styles: MAP_STYLES }}>
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={marker.icon}
            onClick={() => setActiveMarkerID(marker.id)}
          />
        ))}
        {polylines.map((polyline) => (
          <Polyline
            key={polyline.id}
            path={polyline.path}
            options={{ strokeColor: polyline.color, strokeWeight: 2, strokeOpacity: 0.8 }}
          />
        ))}
        {activeMarker && (
          <InfoWindow position={activeMarker.position} onCloseClick={() => setActiveMarkerID(undefined)}>
            <div className="preformatted">{activeMarker.notes}</div>
          </InfoWindow>
        )}
      </GoogleMapWithDOM>
    </>
  );
};
